import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { SearchInputOption } from "../../../shared/components/search-bar/search-input/search-input.component";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { BulkUrlType } from "../../../shared/types/bulk-upload/bulk-url.type";
import { removePropsWithValues } from "../../../shared/utils/objects/object";
import { Company } from "../companies/company.type";
import { User } from "../users/user.type";
import { Partner } from "./partner.type";

@Injectable({
  providedIn: "root",
})
export class PartnerService {
  basePath = `${environment.serverUrl}/partners`;
  basePathCsvQueue = `${this.basePath}/queue-csv`;

  constructor(private http: HttpClient) {}

  createPartner(data: Partner): Observable<Partner> {
    return this.http.post<Partner>(this.basePath, data).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getPartners(options?: QueryParams<Partner>): Observable<PaginatedResponse<Partner>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Partner>>(this.basePath, { params }).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getPartnersPost(options?: QueryParams<Partner>): Observable<PaginatedResponse<Partner>> {
    const body = { ...DEFAULT_QUERY_PARAMS, ...options };
    return this.http.post<PaginatedResponse<Partner>>(`${this.basePath}/get-many`, body).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getPartner(partnerId: string, options?: QueryParams<Partner>): Observable<Partner> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<Partner>(`${this.basePath}/${partnerId}`, { params }).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updatePartner(partnerId: string, data): Observable<Partner> {
    return this.http.put<Partner>(`${this.basePath}/${partnerId}`, data).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  deletePartner(partnerId: string): Observable<Partner> {
    return this.http.delete<Partner>(`${this.basePath}/${partnerId}`).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getCompaniesByQuery(partnerId: string, options?: QueryParams<Company>): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    options = removePropsWithValues(options);
    for (const key in options) {
      params = params.set(key, JSON.stringify(options[key]));
    }
    return this.http.get<PaginatedResponse<any>>(`${this.basePath}/${partnerId}/companies`, { params }).pipe(
      tap(() => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getPutSignedUrl(): Observable<BulkUrlType> {
    return this.http.get<any>(`${this.basePath}/batchs/bulk-upload-url`).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  uploadBatch(url: string, csvFile: File) {
    return this.http.put<any>(url, csvFile);
  }

  getSearchablePartners(options?: QueryParams<Partner>): Observable<SearchInputOption[]> {
    return this.getPartners(options).pipe(
      map((r: PaginatedResponse<Partner>) =>
        r.items.map((partner: Partner) => ({ auxValue: partner.id, value: partner.name, label: partner.name })),
      ),
    );
  }

  getPartnersQueue(options?: QueryParams<Partner>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getAvailableUsersToLink(partnerId: string, options?: QueryParams<User>): Observable<PaginatedResponse<User>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http
      .get<PaginatedResponse<User>>(`${this.basePath}/${partnerId}/available-users-to-link`, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  linkUsers(id: string, data): Observable<void> {
    return this.http.post<void>(`${this.basePath}/${id}/users`, data).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => throwError(error);
  }
}
